import { AUTH_TYPE, AWSAppSyncClient } from 'aws-appsync/lib';
import { DocumentNode } from 'graphql';
import { appConfiguration } from '../../configuration/appConfiguration';
import { User } from '../../login/loginSlice';
import {
  GRAPHQL_SUBSCRIPTION_ERROR_MESSAGE_KEY,
  GRAPHQL_SUBSCRIPTION_ERROR_TITLE_KEY,
  notifyDismissible,
} from '../../components/showNotification';
import { IdGetterObj } from 'apollo-cache-inmemory/lib/types';
import { CustomFormattedMessage } from '../../i18n/i18n';

export enum GraphqlRequestType {
  QUERY = 'QUERY',
  MUTATION = 'MUTATION',
}

export type GraphQlRequest = {
  type: GraphqlRequestType;
  definition: DocumentNode;
};

export type Subscription = {
  definition: DocumentNode;
};

export type GraphQlOperation<T extends GraphQlRequest | Subscription> = {
  user: User;
  request: T;
  requestParams?: object;
};

export const createAppSyncClient = (user: User) => {
  return new AWSAppSyncClient({
    url: appConfiguration.appSync.url,
    region: appConfiguration.appSync.region,
    disableOffline: true,
    auth: {
      type: AUTH_TYPE.OPENID_CONNECT,
      jwtToken: `Bearer ${user.accessToken}`,
    },
    // disable caching, so objects with nullable IDs can be processed
    cacheOptions: {
      dataIdFromObject: (value: IdGetterObj) => null,
    },
  });
};

export const graphqlBaseSubscription: <T>(
  subscriptionData: GraphQlOperation<Subscription>,
  updateHandler: (value: T) => void,
) => void = (subscriptionData, updateHandler) => {
  try {
    const { user, request, requestParams } = subscriptionData;

    const client = createAppSyncClient(user);
    const observable = client.subscribe({
      query: request.definition,
      variables: requestParams,
    });

    observable.subscribe({
      next: updateHandler,
      error: () =>
        notifyDismissible(
          'error',
          <CustomFormattedMessage
            id={GRAPHQL_SUBSCRIPTION_ERROR_MESSAGE_KEY}
          />,
          GRAPHQL_SUBSCRIPTION_ERROR_TITLE_KEY,
        ),
    });
  } catch (error) {
    notifyDismissible(
      'error',
      <CustomFormattedMessage id={GRAPHQL_SUBSCRIPTION_ERROR_MESSAGE_KEY} />,
      GRAPHQL_SUBSCRIPTION_ERROR_TITLE_KEY,
    );
  }
};
